import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const TimerContext = createContext();

const STORAGE_KEY = 'timer.state';

export const TimerProvider = ({ children }) => {
  const [timerCaseId, setTimerCaseId] = useState('');
  const [timerCaseTitle, setTimerCaseTitle] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && saved.isRunning && saved.startTime) {
        const start = new Date(saved.startTime);
        const elapsed = Math.floor((Date.now() - start.getTime()) / 1000);
        setTimerCaseId(saved.caseId || '');
        setTimerCaseTitle(saved.caseTitle || '');
        setTimerStartTime(start);
        setIsTimerRunning(true);
        setElapsedTime(elapsed);
      }
    } catch { /* ignore corrupt data */ }
  }, []);

  // Persist to localStorage whenever running state changes
  useEffect(() => {
    if (isTimerRunning && timerStartTime) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        isRunning: true,
        startTime: timerStartTime.toISOString(),
        caseId: timerCaseId,
        caseTitle: timerCaseTitle,
      }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isTimerRunning, timerStartTime, timerCaseId, timerCaseTitle]);

  // Tick interval
  useEffect(() => {
    if (isTimerRunning && timerStartTime) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - timerStartTime.getTime()) / 1000));
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isTimerRunning, timerStartTime]);

  const startTimer = useCallback((caseId, caseTitle) => {
    const start = new Date();
    setTimerCaseId(caseId);
    setTimerCaseTitle(caseTitle || '');
    setTimerStartTime(start);
    setIsTimerRunning(true);
    setElapsedTime(0);
  }, []);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsTimerRunning(false);
    const result = { caseId: timerCaseId, caseTitle: timerCaseTitle, startTime: timerStartTime, endTime: new Date(), elapsed: elapsedTime };
    setElapsedTime(0);
    setTimerStartTime(null);
    setTimerCaseId('');
    setTimerCaseTitle('');
    return result;
  }, [timerCaseId, timerCaseTitle, timerStartTime, elapsedTime]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsTimerRunning(false);
    setElapsedTime(0);
    setTimerStartTime(null);
    setTimerCaseId('');
    setTimerCaseTitle('');
  }, []);

  const formatTime = useCallback((seconds) => {
    const s = seconds || elapsedTime;
    const h = Math.floor(s / 3600).toString().padStart(2, '0');
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${h}:${m}:${sec}`;
  }, [elapsedTime]);

  return (
    <TimerContext.Provider value={{
      timerCaseId, timerCaseTitle,
      isTimerRunning, timerStartTime, elapsedTime,
      startTimer, stopTimer, resetTimer, formatTime,
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => useContext(TimerContext);
export default TimerContext;
